import outputs from '../../../../resources/outputs';
import { ConfigValue } from '../../../../resources/user_config';
import Util from '../../../../resources/util';
import ZoneId from '../../../../resources/zone_id';
import { RaidbossData } from '../../../../types/data';
import { LocaleText, TriggerSet } from '../../../../types/trigger';

const strikingDummyNames: LocaleText = {
  en: 'Striking Dummy',
  de: 'Trainingspuppe',
  fr: 'Mannequin d\'entraînement',
  ja: '木人',
  cn: '木人',
  ko: '나무인형',
  tc: '木人',
};

export type ConfigIds = 'testTriggerOutput';

export interface Data extends RaidbossData {
  triggerSetConfig: { [key in ConfigIds]: ConfigValue };
  delayedDummyTimestampBefore: number;
  delayedDummyTimestampAfter: number;
  pokes: number;
  watchingForCast: boolean;
}

const triggerSet: TriggerSet<Data> = {
  id: 'CactbotTest',
  zoneId: ZoneId.MiddleLaNoscea,
  zoneLabel: {
    en: 'Cactbot Test Triggers',
    de: 'Cactbot Test Trigger',
    fr: 'Test des triggers Cactbot',
    cn: 'Cactbot测试触发器',
    ko: 'Cactbot 테스트 트리거',
    tc: 'Cactbot測試觸發器',
  },
  comments: {
    en:
      'Testing/troubleshooting triggers (See: <a href="https://github.com/OverlayPlugin/cactbot/blob/main/docs/FAQ-Troubleshooting.md#summerford-farms-raidboss-test">Summerford Farms Raidboss Test</a>)',
    de:
      'Testen/Fehlerbehebung der Tigger (Siehe: <a href="https://github.com/OverlayPlugin/cactbot/blob/main/docs/FAQ-Troubleshooting.md#summerford-farms-raidboss-test">Sommerfurt-Höfe Raidboss Test</a>)',
    fr:
      'Test/Dépannage des triggers (voir : <a href="https://github.com/OverlayPlugin/cactbot/blob/main/docs/FAQ-Troubleshooting.md#summerford-farms-raidboss-test">Vergers d\'Estival Raidboss Test</a>)',
    cn:
      '测试/排错触发器（参见：<a href="https://github.com/OverlayPlugin/cactbot/blob/main/docs/FAQ-Troubleshooting.md#summerford-farms-raidboss-test">Summerford Farms Raidboss Test</a>',
    ko:
      '테스트/문제확인 트리거 (참고: <a href="https://github.com/OverlayPlugin/cactbot/blob/main/docs/FAQ-Troubleshooting.md#summerford-farms-raidboss-test">여름여울 농장 Raidboss 테스트</a>)',
    tc:
      '測試/排錯觸發器（參見：<a href="https://github.com/OverlayPlugin/cactbot/blob/main/docs/FAQ-Troubleshooting.md#summerford-farms-raidboss-test">Summerford Farms Raidboss Test</a>',
  },
  config: [
    {
      id: 'testTriggerOutput',
      name: {
        en: 'Output for "/echo cactbot test config"',
        de: 'Ausgabe für "/echo cactbot test config"',
        fr: 'Sortie pour "/echo cactbot test config"',
        ja: '"/echo cactbot test config"のアウトプット',
        cn: '输出 "/echo cactbot测试配置"',
        ko: '"/echo cactbot 설정 테스트" 출력값',
        tc: '輸出 "/echo cactbot測試配置',
      },
      type: 'string',
      default: () => {
        // Test default function.
        return 'Unset Option';
      },
    },
  ],
  timelineFile: 'test.txt',
  // timeline here is additions to the timeline.  They can
  // be strings, or arrays of strings, or functions that
  // take the same data object (including role and lang)
  // that triggers do.
  timeline: [
    'alerttext "Final Sting" before 4 "oh no final sting in 4"',
    'alarmtext "Death" before 3',
    'alertall "Long Castbar" before 1 speak "voice" "long"',
    (data) => {
      if (data.role !== 'tank' && data.role !== 'healer')
        return 'hideall "Super Tankbuster"';
      return 'alarmtext "Super Tankbuster" before 2';
    },
    (data) => {
      if (data.role !== 'dps')
        return 'hideall "Pentacle Sac (DPS)"';
    },
    (data) => {
      if (data.role !== 'healer')
        return 'hideall "Almagest"';
      return 'alarmtext "Almagest" before 0';
    },
    (data) => {
      return [
        `40 "Death To ${data.me}!!"`,
        'hideall "Death"',
      ];
    },
  ],
  initData: () => {
    return {
      delayedDummyTimestampBefore: 0,
      delayedDummyTimestampAfter: 0,
      pokes: 0,
      watchingForCast: false,
    };
  },
  timelineStyles: [
    {
      regex: /^Death To/,
      style: {
        'color': 'red',
        'font-family': 'Impact',
      },
    },
  ],
  timelineTriggers: [
    {
      id: 'Test Angry Dummy',
      regex: /Angry Dummy/,
      beforeSeconds: 2,
      infoText: (_data, _matches, output) => output.stack!(),
      tts: (_data, _matches, output) => output.stackTTS!(),
      outputStrings: {
        stack: {
          en: 'Stack for Angry Dummy',
          de: 'Sammeln für Wütender Dummy',
          fr: 'Packez-vous pour le Mannequin en colère',
          ja: '怒る木人に集合',
          cn: '木人处集合',
          ko: '화난 나무인형에 집합',
          tc: '木人處集合',
        },
        stackTTS: {
          en: 'Stack',
          de: 'Sammeln',
          fr: 'Packez-vous',
          ja: '集合',
          cn: '集合',
          ko: '집합',
          tc: '集合',
        },
      },
    },
    {
      id: 'Test Delayed Dummy',
      regex: /Angry Dummy/,
      beforeSeconds: 0,
      // Add in a huge delay to make it obvious the delay runs before promise.
      delaySeconds: 10,
      promise: (data) => {
        data.delayedDummyTimestampBefore = Date.now();
        return new Promise<void>((res) => {
          window.setTimeout(() => {
            data.delayedDummyTimestampAfter = Date.now();
            res();
          }, 3000);
        });
      },
      infoText: (data, _matches, output) => {
        const elapsed = data.delayedDummyTimestampAfter - data.delayedDummyTimestampBefore;
        return output.elapsed!({ elapsed: elapsed });
      },
      outputStrings: {
        elapsed: {
          en: 'Elapsed ms: ${elapsed}',
          de: 'Abgelaufene ms: ${elapsed}',
          fr: 'Expiré ms: ${elapsed}',
          ja: '経過時間：${elapsed}',
          cn: '经过时间：${elapsed}',
          ko: '경과 시간: ${elapsed}',
          tc: '經過時間：${elapsed}',
        },
      },
    },
  ],
  triggers: [
    {
      id: 'Test Poke',
      type: 'GameLog',
      netRegex: { line: 'You poke the striking dummy.*?', capture: false },
      preRun: (data) => ++data.pokes,
      infoText: (data, _matches, output) => output.poke!({ numPokes: data.pokes }),
      outputStrings: {
        poke: {
          en: 'poke #${numPokes}',
          de: 'stups #${numPokes}',
          fr: 'poussée #${numPokes}',
          ja: 'つつく #${numPokes}',
          cn: '戳 #${numPokes}',
          ko: '${numPokes}번 찌름',
          tc: '戳 #${numPokes}',
        },
      },
    },
    {
      id: 'Test Psych',
      type: 'GameLog',
      netRegex: { line: 'You psych yourself up alongside the striking dummy.*?', capture: false },
      alertText: (_data, _matches, output) => output.text!(),
      tts: {
        en: 'psych',
        de: 'auf gehts',
        fr: 'motivation',
        ja: '活を入れる',
        cn: '激励',
        ko: '힘내라!',
        tc: '激勵',
      },
      outputStrings: {
        text: {
          en: 'PSYCH!!!',
          de: 'AUF GEHTS!!!',
          fr: 'MOTIVATION !!!',
          ja: '活を入れる！！',
          cn: '激励！！',
          ko: '힘내라!!',
          tc: '激勵！！',
        },
      },
    },
    {
      id: 'Test Laugh',
      type: 'GameLog',
      netRegex: { line: 'You burst out laughing at the striking dummy.*?', capture: false },
      suppressSeconds: 5,
      alarmText: (_data, _matches, output) => output.text!(),
      tts: {
        en: 'hahahahaha',
        de: 'hahahahaha',
        fr: 'hahahahaha',
        ja: 'ハハハハハ',
        cn: '哈哈哈哈哈哈',
        ko: '푸하하하하핳',
        tc: '哈哈哈哈哈哈',
      },
      outputStrings: {
        text: {
          en: 'hahahahaha',
          de: 'hahahahaha',
          fr: 'hahahahaha',
          ja: 'ハハハハハ',
          cn: '2333333333',
          ko: '푸하하하하핳',
          tc: '2333333333',
        },
      },
    },
    {
      id: 'Test Clap',
      type: 'GameLog',
      netRegex: { line: 'You clap for the striking dummy.*?', capture: false },
      sound: '../../resources/sounds/freesound/power_up.webm',
      soundVolume: 0.3,
      tts: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'clapity clap',
          de: 'klatschen',
          fr: 'applaudissement',
          ja: '拍手',
          cn: '鼓掌',
          ko: '박수 짝짝짝',
          tc: '鼓掌',
        },
      },
    },
    {
      id: 'Test Lang',
      type: 'GameLog',
      // In game: /echo cactbot lang
      netRegex: { line: 'cactbot lang.*?', code: Util.gameLogCodes.echo, capture: false },
      infoText: (data, _matches, output) => output.text!({ lang: data.parserLang }),
      outputStrings: {
        text: {
          en: 'Language: ${lang}',
          de: 'Sprache: ${lang}',
          fr: 'Langage: ${lang}',
          ja: '言語：${lang}',
          cn: '语言: ${lang}',
          ko: '언어: ${lang}',
          tc: '語言: ${lang}',
        },
      },
    },
    {
      id: 'Test Response',
      type: 'GameLog',
      netRegex: { line: 'cactbot test response.*?', code: Util.gameLogCodes.echo, capture: false },
      response: (_data, _matches, output) => {
        // cactbot-builtin-response
        output.responseOutputStrings = {
          alarmOne: outputs.num1,
          alertTwo: outputs.num2,
          infoThree: outputs.num3,
          ttsFour: outputs.num4,
        };
        return {
          alarmText: output.alarmOne!(),
          alertText: output.alertTwo!(),
          infoText: output.infoThree!(),
          tts: output.ttsFour!(),
        };
      },
    },
    {
      id: 'Test Watch',
      type: 'GameLog',
      netRegex: { line: 'cactbot test watch.*?', code: Util.gameLogCodes.echo, capture: false },
      promise: (data) =>
        Util.watchCombatant({
          names: [
            data.me,
            strikingDummyNames[data.lang] ?? strikingDummyNames['en'],
          ],
          // 50 seconds
          maxDuration: 50000,
        }, (ret) => {
          const me = ret.combatants.find((c) => c.Name === data.me);
          const dummyName = strikingDummyNames[data.lang] ?? strikingDummyNames['en'];
          const dummies = ret.combatants.filter((c) => c.Name === dummyName);
          console.log(`test watch: me = ${me?.Name ?? 'undefined'}; ${dummies.length} dummies`);
          if (me) {
            for (const dummy of dummies) {
              const distX = Math.abs(me.PosX - dummy.PosX);
              const distY = Math.abs(me.PosY - dummy.PosY);
              const dist = Math.hypot(distX, distY);
              console.log(`test watch: distX = ${distX}; distY = ${distY}; dist = ${dist}`);
              if (dist < 5)
                return true;
            }
            return false;
          }
          return false;
        }),
      infoText: (_data, _matches, output) => output.close!(),
      outputStrings: {
        close: {
          en: 'Dummy close!',
          de: 'Puppe beendet!',
          fr: 'Mannequin proche !',
          ja: '木人に近すぎ！',
          cn: '靠近木人！',
          ko: '나무인형과 가까움!',
          tc: '靠近木人！',
        },
      },
    },
    {
      id: 'Test Config',
      type: 'GameLog',
      netRegex: { line: 'cactbot test config.*?', code: Util.gameLogCodes.echo, capture: false },
      alertText: (data, _matches, output) => {
        return output.text!({ value: data.triggerSetConfig.testTriggerOutput.toString() });
      },
      outputStrings: {
        text: {
          en: 'Config Value: ${value}',
          de: 'Einstellungswert: ${value}',
          fr: 'Valeur de configuration : ${value}',
          ja: '設定: ${value}',
          cn: '配置值: ${value}',
          ko: '설정값: ${value}',
          tc: '配置值: ${value}',
        },
      },
    },
    {
      id: 'Test Combatant Cast Enable',
      type: 'GameLog',
      netRegex: {
        line: 'cactbot test combatant cast.*?',
        code: Util.gameLogCodes.echo,
        capture: false,
      },
      run: (data) => {
        data.watchingForCast = true;
      },
    },
    {
      id: 'Test Combatant Cast',
      type: 'CombatantMemory',
      netRegex: {
        pair: [{ key: 'IsCasting1', value: '1' }, { key: 'CastBuffID', value: '.*?' }],
      },
      condition: (data) => data.watchingForCast,
      infoText: (data, matches, output) => {
        data.watchingForCast = false;
        return output.casting!({ id: matches.id, spellId: matches.pairCastBuffID });
      },
      outputStrings: {
        casting: {
          en: 'ID ${id} is casting spell ID ${spellId}',
          de: 'ID ${id} wirkt Zauber ID ${spellId}',
          fr: 'ID ${id} incante le sort ID ${spellId}',
          ja: 'ID ${id} スペルID ${spellId}を詠唱中',
          cn: 'ID ${id} 正在施法 ID ${spellId}',
          ko: 'ID ${id}: 스킬 ID ${spellId}를 시전하는 중',
          tc: 'ID ${id} 正在施法 ID ${spellId}',
        },
      },
    },
    {
      id: 'Test Countdown',
      type: 'Countdown',
      netRegex: { result: '00' },
      countdownSeconds: (_data, matches) => parseFloat(matches.countdownTime),
      infoText: (_data, matches, output) =>
        output.countdown!({ player: matches.name, seconds: matches.countdownTime }),
      outputStrings: {
        countdown: {
          en: '${player} started ${seconds}s countdown',
          // or inline: '${player} started {{CD}} countdown',
          de: '${player} startet ${seconds}s countdown',
          fr: '${player} a démarré un compte à rebours de ${seconds}s',
          ja: '${player} が ${seconds} 秒のカウントダウンを開始しました',
          cn: '${player} 开始倒计时 ${seconds}秒',
          ko: '${player} ${seconds}초 초읽기를 시작했습니다',
          tc: '${player} 開始倒數計時 ${seconds}秒',
        },
      },
    },
    {
      id: 'Test Countdown Failed',
      type: 'Countdown',
      netRegex: { result: '(?!00).{2}' },
      infoText: (_data, matches, output) =>
        output.countdownFail!({ player: matches.name, code: matches.result }),
      outputStrings: {
        countdownFail: {
          en: '${player} failed to start countdown (result code: ${code})',
          de: '${player} konnte Countdown nicht starten (Ergebniscode: ${code})',
          fr: '${player} a échoué à démarrer un compte à rebours (result code: ${code})',
          ja: '${player} がカウントダウンを開始できませんでした (コード: ${code})',
          cn: '${player} 开始倒计时失败 (结果代码: ${code})',
          ko: '${player} 초읽기를 시작하지 못했습니다 (반환 코드: ${code})',
          tc: '${player} 開始倒數計時失敗 (結果代碼: ${code})',
        },
      },
    },
    {
      id: 'Test Countdown Cancel',
      type: 'CountdownCancel',
      netRegex: {},
      infoText: (_data, matches, output) => output.countdownCancel!({ player: matches.name }),
      outputStrings: {
        countdownCancel: {
          en: '${player} cancelled countdown',
          de: '${player} hat den Countdown abgebrochen',
          fr: '${player} a annulé le compte à rebours',
          ja: '${player} がカウントダウンをキャンセルしました',
          cn: '${player} 取消倒计时',
          ko: '${player} 초읽기를 취소했습니다',
          tc: '${player} 取消倒數計時',
        },
      },
    },
    {
      id: 'Test OutputStrings',
      type: 'GameLog',
      netRegex: {
        line: 'cactbot test outputStrings',
        code: Util.gameLogCodes.echo,
        capture: false,
      },
      infoText: (data, _matches, output) => {
        // TODO: This doesn't work unless you're in a party, because party tracker is empty.
        // OverlayPlugin should probably always return the current player as being in the party
        // regardless of the rest of the party composition.
        return output.text!({ player: data.party.member(data.me) });
      },
      outputStrings: {
        text: {
          en: 'player = ${player}, player.job = ${player.job}, player.bogus = ${player.bogus}',
          de: 'player = ${player}, player.job = ${player.job}, player.bogus = ${player.bogus}',
          fr: 'player = ${player}, player.job = ${player.job}, player.bogus = ${player.bogus}',
          ja: 'player = ${player}, player.job = ${player.job}, player.bogus = ${player.bogus}',
          cn: 'player = ${player}, player.job = ${player.job}, player.bogus = ${player.bogus}',
          ko: 'player = ${player}, player.job = ${player.job}, player.bogus = ${player.bogus}',
          tc: 'player = ${player}, player.job = ${player.job}, player.bogus = ${player.bogus}',
        },
      },
    },
    {
      id: 'Test Trigger Countdown',
      type: 'GameLog',
      netRegex: {
        line: 'cactbot test trigger countdown.*?',
        code: Util.gameLogCodes.echo,
        capture: false,
      },
      delaySeconds: 2,
      durationSeconds: 7, // should cause countdown to be displayed at 0.0 for 2 seconds.
      countdownSeconds: 5,
      infoText: (_data, _matches, output) => {
        return output.text!();
      },
      outputStrings: {
        text: {
          en: 'Trigger countdown test',
          de: 'Trigger countdown test',
          fr: 'Test de décompte des triggers',
          cn: '触发器倒计时测试',
          ko: '트리거 카운트다운 테스트',
          tc: '觸發器倒數計時測試',
        },
      },
    },
  ],
  timelineReplace: [
    {
      locale: 'de',
      missingTranslations: true,
      replaceSync: {
        'You bid farewell to the striking dummy': 'Du winkst der Trainingspuppe zum Abschied zu',
        'You bow courteously to the striking dummy':
          'Du verbeugst dich hochachtungsvoll vor der Trainingspuppe',
        'test sync': 'test sync',
        'You burst out laughing at the striking dummy': 'Du lachst herzlich mit der Trainingspuppe',
        'cactbot lang': 'cactbot sprache',
        'cactbot test response': 'cactbot test antwort',
        'cactbot test watch': 'cactbot test beobachten',
        'cactbot test config': 'cactbot test konfig',
        'cactbot test outputStrings': 'cactbot test outputStrings',
        'cactbot test combatant cast': 'cactbot test gegner wirken',
        'testNetRegexTimeline': 'testNetRegexTimeline',
        'You clap for the striking dummy': 'Du klatschst begeistert Beifall für die Trainingspuppe',
        'You psych yourself up alongside the striking dummy':
          'Du willst wahren Kampfgeist in der Trainingspuppe entfachen',
        'You poke the striking dummy': 'Du stupst die Trainingspuppe an',
      },
      replaceText: {
        'Final Sting': 'Schlussstich',
        'Almagest': 'Almagest',
        'Angry Dummy': 'Wütender Dummy',
        'Long Castbar': 'Langer Zauberbalken',
        'Dummy Stands Still': 'Dummy still stehen',
        'Death': 'Tot',
        'Super Tankbuster': 'Super Tankbuster',
        'Pentacle Sac': 'Pentacle Sac',
        'Engage': 'Start!',
        'Two': 'Zwei',
        '(?<! )Three': 'Drei',
        'Four': 'Vier',
        'Six': 'Sechs',
        'Ten(?!d)': 'Zehn',
        'Fifteen': 'Fünfzehn',
        'Force Jump Three': 'Gewaltsam auf Drei Springen',
        'Invisible': 'Unsichtbar',
      },
    },
    {
      locale: 'fr',
      missingTranslations: true,
      replaceSync: {
        'cactbot lang': 'cactbot langue',
        'cactbot test response': 'cactbot test de réponse',
        'cactbot test watch': 'cactbot test d\'observation',
        'cactbot test config': 'test de configuration de cactbot',
        'cactbot test combatant cast': 'test d\'incantation d\'un combattant',
        'cactbot test outputStrings': 'cactbot test outputStrings',
        'testNetRegexTimeline': 'testNetRegexTimeline',
        'You bid farewell to the striking dummy':
          'Vous faites vos adieux au mannequin d\'entraînement',
        'You bow courteously to the striking dummy':
          'Vous vous inclinez devant le mannequin d\'entraînement',
        'You burst out laughing at the striking dummy':
          'Vous vous esclaffez devant le mannequin d\'entraînement',
        'You clap for the striking dummy': 'Vous applaudissez le mannequin d\'entraînement',
        'You poke the striking dummy':
          'Vous touchez légèrement le mannequin d\'entraînement du doigt',
        'You psych yourself up alongside the striking dummy':
          'Vous vous motivez devant le mannequin d\'entraînement',
        'test sync': 'test sync',
      },
      replaceText: {
        'Almagest': 'Almageste',
        'Angry Dummy': 'Mannequin en colère',
        'Death': 'Mort',
        'Death To': 'Mort sur',
        'Dummy Stands Still': 'Mannequin immobile',
        'Engage': 'À l\'attaque',
        'Final Sting': 'Dard final',
        'Long Castbar': 'Longue barre de lancement',
        'Pentacle Sac': 'Pentacle Sac',
        'Super Tankbuster': 'Super Tank buster',
        'Two': 'Deux',
        '(?<! )Three': 'Trois',
        'Four': 'Quatre',
        'Six': 'Six',
        'Ten': 'Dix',
        'Fifteen': 'Quinze',
        'Force Jump Three': 'Saut forcé à trois',
        'Invisible': 'Invisible',
      },
    },
    {
      locale: 'ja',
      missingTranslations: true,
      replaceSync: {
        'You bid farewell to the striking dummy': '.*は木人に別れの挨拶をした',
        'You bow courteously to the striking dummy': '.*は木人にお辞儀した',
        'test sync': 'test sync',
        'testNetRegexTimeline': 'testNetRegexTimeline',
        'You burst out laughing at the striking dummy': '.*は木人のことを大笑いした',
        'cactbot lang': 'cactbot言語',
        'cactbot test response': 'cactbotレスポンステスト',
        'cactbot test watch': 'cactbot探知テスト',
        'cactbot test config': 'cactbot設定テスト',
        'cactbot test combatant cast': 'cactbotターゲットキャストテスト',
        'cactbot test outputStrings': 'cactbot outputStrings テスト',
        'You clap for the striking dummy': '.*は木人に拍手した',
        'You psych yourself up alongside the striking dummy': '.*は木人に活を入れた',
        'You poke the striking dummy': '.*は木人をつついた',
      },
      replaceText: {
        'Almagest': 'アルマゲスト',
        'Angry Dummy': '怒る木人',
        'Death': '死',
        'Death To': '死の宣告',
        'Dummy Stands Still': '木人はじっとしている',
        'Engage': '戦闘開始',
        'Final Sting': 'ファイナルスピア',
        'Long Castbar': '長い長い詠唱バー',
        'Pentacle Sac': 'ナイサイ',
        'Super Tankbuster': 'スーパータンクバスター',
        'Two': '二',
        '(?<! )Three': '三',
        'Four': '四',
        'Six': '六',
        'Ten': '十',
        'Fifteen': '十五',
        'Force Jump Three': '三に強制ジャンプ',
        'Invisible': '見えないはず',
      },
    },
    {
      locale: 'cn',
      replaceSync: {
        'You bid farewell to the striking dummy': '.*向木人告别',
        'You bow courteously to the striking dummy': '.*恭敬地对木人行礼',
        'test sync': 'test sync',
        'testNetRegexTimeline': 'testNetRegexTimeline',
        'You burst out laughing at the striking dummy': '.*看着木人高声大笑',
        'cactbot lang': 'cactbot语言',
        'cactbot test combatant cast': 'cactbot测试战斗员施法',
        'cactbot test config': 'cactbot测试配置',
        'cactbot test outputStrings': 'cactbot测试输出字符串',
        'cactbot test response': 'cactbot响应测试',
        'cactbot test trigger countdown': 'cactbot测试触发器倒计时',
        'cactbot test watch': 'cactbot探测测试',
        'You clap for the striking dummy': '.*向木人送上掌声',
        'You psych yourself up alongside the striking dummy': '.*激励木人',
        'You poke the striking dummy': '.*用手指戳向木人',
      },
      replaceText: {
        'Final Sting': '终极针',
        'Almagest': '至高无上',
        'Angry Dummy': '愤怒的木人',
        'Long Castbar': '长时间咏唱',
        'Dummy Stands Still': '木人8动了',
        'Super Tankbuster': '超级无敌转圈死刑',
        'Death To': '嗝屁攻击：',
        'Death': '嗝屁',
        'Engage': '战斗开始',
        'Pentacle Sac': '传毒',
        'Two': '贰',
        '(?<! )Three': '叁',
        'Four': '肆',
        'Six': '陆',
        'Ten': '拾',
        'Fifteen': '拾伍',
        'Force Jump Three': '强制跳转叁',
        'Invisible': '不可见',
      },
    },
    {
      locale: 'tc',
      replaceSync: {
        'You bid farewell to the striking dummy': '.*向木人告别',
        'You bow courteously to the striking dummy': '.*恭敬地對木人行禮',
        'test sync': 'test sync',
        'testNetRegexTimeline': 'testNetRegexTimeline',
        'You burst out laughing at the striking dummy': '.*看着木人高聲大笑',
        'cactbot lang': 'cactbot語言',
        'cactbot test combatant cast': 'cactbot測試戰鬥員施法',
        'cactbot test config': 'cactbot測試配置',
        'cactbot test outputStrings': 'cactbot測試輸出字符串',
        'cactbot test response': 'cactbot響應測試',
        'cactbot test trigger countdown': 'cactbot測試觸發器倒計時',
        'cactbot test watch': 'cactbot探測測試',
        'You clap for the striking dummy': '.*向木人送上掌聲',
        'You psych yourself up alongside the striking dummy': '.*激勵木人',
        'You poke the striking dummy': '.*用手指戳向木人',
      },
      replaceText: {
        'Final Sting': '最終刺針',
        'Almagest': '至高無上',
        'Angry Dummy': '憤怒的木人',
        'Long Castbar': '長時間詠唱',
        'Dummy Stands Still': '木人8動了',
        'Super Tankbuster': '超級無敵轉圈死刑',
        'Death To': '嗝屁攻擊：',
        'Death': '嗝屁',
        'Engage': '戰鬥開始',
        'Pentacle Sac': '傳毒',
        'Two': '貳',
        '(?<! )Three': '叄',
        'Four': '肆',
        'Six': '陸',
        'Ten': '拾',
        'Fifteen': '拾伍',
        'Force Jump Three': '強制跳轉叄',
        'Invisible': '不可見',
      },
    },
    {
      locale: 'ko',
      replaceSync: {
        'You bid farewell to the striking dummy': '.*나무인형에게 작별 인사를 합니다',
        'You bow courteously to the striking dummy': '.*나무인형에게 공손하게 인사합니다',
        'test sync': '테스트 싱크',
        'testNetRegexTimeline': 'testNetRegexTimeline',
        'You burst out laughing at the striking dummy': '.*나무인형을 보고 폭소를 터뜨립니다',
        'cactbot test config': 'cactbot 설정 테스트',
        'cactbot lang': 'cactbot 언어',
        'cactbot test response': 'cactbot 응답 테스트',
        'cactbot test watch': 'cactbot 탐지 테스트',
        'cactbot test combatant cast': 'cactbot 스킬 시전 테스트',
        'cactbot test outputStrings': 'cactbot outputStrings 테스트',
        'cactbot test trigger countdown': 'cactbot 트리거 카운트다운 테스트',
        'You clap for the striking dummy': '.*나무인형에게 박수를 보냅니다',
        'You psych yourself up alongside the striking dummy': '.*나무인형에게 힘을 불어넣습니다',
        'You poke the striking dummy': '.*나무인형을 쿡쿡 찌릅니다',
      },
      replaceText: {
        'Final Sting': '마지막 벌침',
        'Almagest': '알마게스트',
        'Angry Dummy': '화난 나무인형',
        'Long Castbar': '긴 시전바',
        'Dummy Stands Still': '나무인형이 아직 살아있다',
        'Death': '데스',
        'Super Tankbuster': '초강력 탱크버스터',
        'Pentacle Sac': 'Pentacle Sac',
        'Engage': '시작',
        'Two': '2',
        '(?<! )Three': '3',
        'Four': '4',
        'Six': '6',
        'Ten': '10',
        'Fifteen': '15',
        'Force Jump Three': '3으로 돌아가기',
        'Invisible': '타임라인 숨기기',
      },
    },
  ],
};

export default triggerSet;
